import { useState, useEffect, useCallback } from 'react';

/**
 * Async state enum
 * @readonly
 * @enum {string}
 */
export const asyncState = Object.freeze({
    IDLE: 'idle',
    PENDING: 'pending',
    SUCCESS: 'success',
    ERROR: 'error'
});

/**
 * @typedef AsyncManager
 * @property {() => any} execute Calls the wrapped async function with status and error handling
 * @property {string} status @see asyncState 
 * @property {*} value The return value of the async function
 * @property {*} error The error thrown by the async function
 *
 * Adds some asynchronous handling to the provided function
 * 
 * This hook encapsulates state handling for asynchronous functions
 * @param {Promise<any>} asyncFunction The asynchronous function to invoke
 * @param {boolean} immediate Should the async function rbe invoked immediately?
 * @returns {AsyncManager} 
 */
export const useAsync = (asyncFunction, immediate = true) => {
    const [status, setStatus] = useState(asyncState.IDLE);
    const [value, setValue] = useState(null);
    const [error, setError] = useState(null);

    const execute = useCallback((params) => {
        setStatus(asyncState.PENDING);
        setValue(null);
        setError(null);

        return asyncFunction(params)
            .then((response) => {
                setValue(response);
                setStatus(asyncState.SUCCESS);
            })
            .catch(err => {
                setError(err);
                setStatus(asyncState.ERROR);
            });
    }, [asyncFunction]);

    useEffect(() => {
        if (immediate) {
            execute();
        }
    }, [execute, immediate]);

    return { execute, status, value, error };
};